import axios from 'axios'
import Genre from '../@types/Genre.types'
import { ApiHttpClientParams, ApiHttpParams } from '../@types/Utils.types'
import ApiUtils from './api.utils'

export const __application_environment =
  process.env.NEXT_PUBLIC_DEPLOYMENT_GROUP
export const API_BASE_URL =
  __application_environment === 'Production'
    ? 'https://api.raptures.io'
    : 'https://api-staging.raptures.io'

export const APP_BASE_URL =
  __application_environment === 'Production'
    ? 'https://app.raptures.io'
    : 'https://app-staging.raptures.io'

export const STRIPE_PUBLIC_API_KEY =
  __application_environment === 'Production'
    ? 'pk_live_51K3ksmDJugZgb6BveW0pg7IJR3u1oRL30e9CktzaLRH1PZjDHqhXWg8Nl7HWbF4w9m5B4PGI46e7Skd1vpwaLsf400zKCE3kwa'
    : 'pk_test_51K5JcqEexlX9H8temM2IObAjvnRPmmsSLCfoDfmjwmCfMLkvJVuIM8GlUP0AWrFw1TJIfINbZ3omkhj3mzvruAP700ODL2OhW1'

export const SEGMENT_WRITE_KEY =
  __application_environment === 'Production'
    ? 'fhVAF5ePA2G9oNBu60BxhIgsH1YyqH8j'
    : 'vKO8bRaNzpOUXGQ8AXUDskRu5T9dNRno'

export const HTTP_INSECURE = axios.create({
  baseURL: API_BASE_URL + '/api/',
})

export async function ApiHttp({ req, res }: ApiHttpParams) {
  const access_token = req.cookies.access_token
  let guest_id = req.cookies.guest_id
  const forwarded = req.headers['x-forwarded-for']
  const ip =
    (forwarded
      ? forwarded.toString().split(/, /)[0]
      : req.socket.remoteAddress) || ''
  if (!guest_id) {
    const { data } = await HTTP_INSECURE.post(ApiUtils.Endpoints.guest.source)
    ApiUtils.SetCookie(
      res,
      'guest_id',
      data.data,
      ApiUtils.CookieOptionsDefault(
        getLocaleTime(1000 * 60 * 60 * 24 * 365 * 10)
      )
    )
    guest_id = data.data.guestId
  }
  if (access_token) {
    return axios.create({
      baseURL: API_BASE_URL + '/api/',
      headers: {
        Authorization: 'Bearer ' + access_token,
        'X-Raptures-User-Ip': ip,
      },
    })
  }
  return axios.create({
    baseURL: API_BASE_URL + '/api/',
    params: { guest: guest_id },
    headers: {
      'X-Raptures-User-Ip': ip,
    },
  })
}

export async function ApiHttpClient({
  req,
  res,
  path,
  method,
  body,
  params,
}: ApiHttpClientParams) {
  let [error, result] = [undefined, undefined]
  try {
    const response = await (
      await ApiHttp({ req, res })
    )[method || 'get'](
      path,
      body || {
        params,
      }
    )
    // console.log('Response ========================')
    // console.log(response.request)
    result = response.data
  } catch (err: any) {
    // console.log('Error ========================')
    // console.log(err.response)
    error = err.response?.data?.message || err.message
  }
  return {
    result: result as unknown as Record<string, any>,
    error: error ? new Error(error) : undefined,
  } as {
    result?: Record<string, any>
    error?: Error
  }
}

type HttpClientParams = {
  path: string
  method?: ApiHttpClientParams['method']
  body?: any
  params?: any
}

export async function HttpClient({
  path,
  method,
  body,
  params,
}: HttpClientParams) {
  let [error, result] = [undefined, undefined]
  try {
    result = (
      await axios[method || 'get'](
        path,
        body || {
          params,
        }
      )
    ).data
  } catch (err: any) {
    error = err.response?.data?.message || err.message
  }
  return {
    result: result as unknown as Record<string, any>,
    error: error ? new Error(error) : undefined,
  } as {
    result?: Record<string, any>
    error?: Error
  }
}

export function getPlaceholderArray(count: number) {
  const placeholders: number[] = []
  for (let i = 0; i < count; i++) placeholders.push(i + 1)
  return placeholders
}

export function transformGenresForTab(genres: Genre[]) {
  if (!genres.length) return []
  const _genres = genres.map((genre) => {
    return {
      id: genre.name
        .replace(/\//g, ' ')
        .replace(/[\(\),]/g, '')
        .replace(/ /g, '-')
        .toLocaleLowerCase(),
      title: genre.name,
      key: genre.id,
      image: genre.image,
    }
  })

  const result = [_genres[_genres.length - 1], ..._genres.slice(0, -1)]
  return result
}

export function getComicSlugFromTitle(title: string) {
  return title
    ?.replace(/\//g, ' ')
    ?.replace(/[\(\),]/g, '')
    ?.replace(/ /g, '-')
    ?.toLowerCase()
}

export function getTitleFromSlug(title: string) {
  return title
    .replace(/-/g, ' ')
    .replace(/(^\w{1})|(\s+\w{1})/g, (letter) => letter.toUpperCase())
}

export function getPrimaryGenres(genre?: Genre[]) {
  const genres = genre?.slice(0, 2).map((_g) => _g.name)
  return genres?.join(' / ')
}

export function convertStringToValidRegex(string: string) {
  return string.replace(/[-/\\^$*+?.()|[\]{}]/g, '\\$&')
}

export function getLocaleTime(time?: number) {
  return new Date(new Date().getTime() + (time || 0))
  // let expires = new Date().getTime() + (time || 0)
  // expires = new Date(
  //   expires.getTime() - expires.getTimezoneOffset() * 60000 + (time || 0)
  // )
  // return expires
}

export function formatTimeShort(time?: string) {
  if (!time) return ''
  const date = Date.now() - Date.parse(time)
  let createdAt = Math.floor(date / 60000)
  let suffix = 'min'

  if (createdAt > 60) {
    createdAt = Math.floor(createdAt / 60)
    suffix = 'h'
    if (createdAt > 24) {
      createdAt = Math.floor(createdAt / 24)
      suffix = 'd'
      if (createdAt > 7) {
        createdAt = Math.floor(createdAt / 7)
        suffix = 'w'
        if (createdAt > 4) {
          createdAt = Math.floor(createdAt / 4)
          suffix = 'm'
          if (createdAt > 12) {
            createdAt = Math.floor(createdAt / 12)
            suffix = 'y'
          }
        }
      }
    }
  }

  return `${createdAt}${suffix}`
}

export function formatTime(time?: string) {
  if (!time) return ''
  const date = Date.now() - Date.parse(time)
  let createdAt = Math.floor(date / 60000)
  let suffix = 'mins'

  if (createdAt > 60) {
    createdAt = Math.floor(createdAt / 60)
    suffix = 'hrs'
    if (createdAt > 24) {
      createdAt = Math.floor(createdAt / 24)
      suffix = 'days'
      if (createdAt > 7) {
        createdAt = Math.floor(createdAt / 7)
        suffix = createdAt > 1 ? 'weeks' : 'A week'
        if (createdAt > 4) {
          createdAt = Math.floor(createdAt / 4)
          suffix = createdAt > 1 ? 'months' : 'month'
          if (createdAt > 12) {
            createdAt = Math.floor(createdAt / 12)
            suffix = 'yrs'
          }
        }
      }
    }
  }

  return `${createdAt} ${suffix} ago`
}

export function getCleanPageURL() {
  const url = new URL(window.location.href)
  return url.origin + url.pathname
}

export function getCurrencySymbol(currency: string) {
  switch (currency) {
    case 'USD':
      return '$'
    case 'NGN':
      return '₦'
    case 'GBP':
      return '£'
    case 'GHS':
      return '₵'
    case 'ZAR':
      return 'R'
    default:
      return '$'
  }
}
